import { useState, useEffect, useRef } from 'react'
import { useAppSelector } from '../../hooks'
import { getPhaserGameSync } from '../../PhaserGame'
import type Game from '../../scenes/Game'
import { phaserEvents, Event } from '../../events/EventCenter'
import { findReceptionGuestGroup, type GuestGroup } from './receptionGuestGroup'

function getNetwork() {
  const phaserGame = getPhaserGameSync()
  const game = phaserGame?.scene?.keys?.game as Game | undefined
  return game?.network ?? null
}

/**
 * Scans Colyseus player state and returns guest group data when the current
 * user is in a reception room with guests from one of their bookings.
 */
export function useReceptionGuests(): {
  guestGroup: GuestGroup | null
  dismiss: () => void
} {
  const currentVR = useAppSelector((s) => s.virtualRoomFilter.currentVirtualRoom)
  const vrFilters = useAppSelector((s) => s.virtualRoomFilter.filters)
  const todaysBookings = useAppSelector((s) => s.roomBooking.myTodaysBookings)
  const userId = useAppSelector((s) => s.user.userData?.id)

  const [guestGroup, setGuestGroup] = useState<GuestGroup | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const scanKeyRef = useRef('')

  // Reset dismissed when VR changes
  useEffect(() => {
    setDismissed(false)
  }, [currentVR?.roomId])

  // Scan for guests in the same reception VR every 2s
  useEffect(() => {
    if (!currentVR || !userId) {
      setGuestGroup(null)
      return
    }

    const isReception = vrFilters[currentVR.roomId]?.isReception
    if (!isReception) {
      setGuestGroup(null)
      return
    }

    const scan = () => {
      const network = getNetwork()
      const room = network?.room
      if (!room) {
        setGuestGroup(null)
        return
      }

      const myBookingIds = new Set<string>()
      for (const b of todaysBookings) {
        if (b.status === 'CANCELLED') continue
        const isParticipant =
          b.creatorId === userId || b.invitees.some((inv) => inv.userId === userId)
        if (isParticipant && b.objectId) myBookingIds.add(b.objectId)
      }

      const players: [string, any][] = []
      room.state.players.forEach((player: any, sessionId: string) => players.push([sessionId, player]))
      const best = findReceptionGuestGroup(players, currentVR.roomId, myBookingIds, (sid) =>
        network.getBroadcastVirtualRoomId(sid)
      )

      // Only update state when data actually changed
      const key = best
        ? best.bookingId + ':' + best.guests.map((g) => g.sessionId).sort().join(',')
        : ''
      if (key !== scanKeyRef.current) {
        scanKeyRef.current = key
        setGuestGroup(best)
      }
    }

    scan()
    const id = setInterval(scan, 2000)
    // A teleported guest's new room arrives as a VR update; rescan then rather than on the poll.
    phaserEvents.on(Event.VIRTUAL_ROOM_OCCUPANTS_UPDATED, scan)
    return () => {
      clearInterval(id)
      phaserEvents.off(Event.VIRTUAL_ROOM_OCCUPANTS_UPDATED, scan)
      scanKeyRef.current = ''
    }
  }, [currentVR, vrFilters, todaysBookings, userId])

  return {
    guestGroup: dismissed ? null : guestGroup,
    dismiss: () => setDismissed(true),
  }
}
